import { RateType } from "../types/Lookups";
import { bestMatch } from "./fuzzyMatch";
import { chatSenders, withoutChatStamps } from "./stripMessage";

/**
 * Reading a price list into a price sheet (roadmap #30) — the CODE part:
 * well-labelled spreadsheets and simple pasted lines are read here, in the
 * browser, for free (no AI, nothing sent). Photos, scans, handwriting and
 * anything this can't make sense of go to "Read with AI"
 * (POST ai/price-lists/read). Either way the lines are only proposed: the
 * person ticks and corrects them, they land on the sheet unsaved, and the
 * sheet's own Save does the rest.
 */

export type PriceSheetKind = "provider" | "request" | "mission";

/** The sheet's own labels a line can be matched to. */
export interface PriceListOptions {
    services: string[];
    places: string[];
    resources: string[];
}

/** One proposed line (same shape as the server's). */
export interface ProposedPriceLine {
    text: string;
    service: string | null;
    place: string | null;
    resource: string | null;
    unit: RateType | null;
    currency: string | null;
    amount: number | null;
    quantity: number | null;
    /** Picked as the closest match, not written as such on the list — shown as "predicted" to check. */
    predicted?: { service?: boolean; place?: boolean; resource?: boolean };
    /** What the list says around the line: its section heading and the intro ("Our updated Dubai rates"). */
    context?: string;
}

const UNIT_WORDS: Array<[RegExp, RateType]> = [
    [/\b(per\s+)?(hour|hourly|hr|hrs)\b|\/\s*h\b/i, RateType.Hour],
    [/\b(per\s+)?(month|monthly|mo)\b/i, RateType.Month],
    [/\b(per\s+)?(year|yearly|annual|annum)\b/i, RateType.Year],
    [/\b(per\s+)?(trip|transfer|one[- ]way|run|drop)\b/i, RateType.Transfer],
    [/\b(per\s+)?(day|daily|days)\b|\/\s*d\b/i, RateType.Day],
];

const MONTHS = "jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec";

const SYMBOLS: Array<[RegExp, string]> = [
    [/\bUS\$|\$/, "USD"], [/€/, "EUR"], [/£/, "GBP"], [/د\.إ|\bdhs?\b|\bdirhams?\b/i, "AED"], [/\bJD\b|\bdinars?\b/i, "JOD"], [/\bSR\b|\briyals?\b/i, "SAR"],
];
const ISO = /\b(USD|EUR|GBP|AED|SAR|QAR|KWD|BHD|OMR|JOD|EGP|ILS|TRY|IQD|LBP|CHF|CAD|AUD|INR|PKR|KES|NGN|ZAR)\b/i;

/** "1,250.50", "1 250", "1.250,50" → 1250.5 (the last separator followed by 1–2 digits is the decimal). */
export const parseAmount = (raw: string): number | null => {
    const text = raw.replace(/[^\d.,\s]/g, "").trim().replace(/\s+/g, "");
    if (!/\d/.test(text)) return null;
    const decimal = /[.,](\d{1,2})$/.exec(text);
    const whole = (decimal ? text.slice(0, -decimal[0].length) : text).replace(/[.,]/g, "");
    const n = Number(`${whole}${decimal ? `.${decimal[1]}` : ""}`);
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};

/** Units a list can price per that CRX's rates don't have (no hour / day / transfer / month / year). */
const UNKNOWN_UNITS = /^(?:nights?|persons?|people|pax|heads?|guests?|rooms?|kms?|kilomet(?:re|er)s?|miles?|shifts?|weeks?|visits?|events?|items?|pieces?|kgs?)$/i;

/**
 * The unit a line is priced per. A stated "per …" wins over a duration in the
 * description ("full day (10 hrs): 1,250 AED per day" is per day).
 */
export const unitOf = (text: string): RateType | null => {
    const stated = Array.from(text.matchAll(/\bper\s+(\w+)|\/\s*([hd])\b/gi));
    for (const match of [...stated].reverse()) {
        const unit = UNIT_WORDS.find(([pattern]) => pattern.test(match[0]))?.[1];
        if (unit) return unit;
    }
    // priced per something CRX has no unit for ("12-hour shift … per shift", "per night"): left for the person to pick
    if (stated.some((match) => UNKNOWN_UNITS.test(match[1] ?? ""))) return null;
    return UNIT_WORDS.find(([pattern]) => pattern.test(text))?.[1] ?? null;
};

export const currencyOf = (text: string): string | null => {
    const iso = ISO.exec(text);
    if (iso) return iso[1].toUpperCase();
    return SYMBOLS.find(([pattern]) => pattern.test(text))?.[1] ?? null;
};

/**
 * The option a line names, by shared words: the best option whose words mostly
 * appear in the line (at least 60% and a clear winner), else null.
 * @param text - The line as written.
 * @param options - The sheet's labels.
 */
export const matchOption = (text: string, options: string[]): string | null => bestMatch(text, options)?.value ?? null;

/**
 * One proposed line. `context` is what the list says around it: the section
 * heading above it ("Executive Protection – Armed") helps name the service,
 * and a place named once at the top ("Our Dubai rates") is every line's place
 * unless the line names its own.
 */
const line = (text: string, amount: number | null, options: PriceListOptions, extra: Partial<ProposedPriceLine> = {}, context: { heading?: string; intro?: string } = {}): ProposedPriceLine => {
    // the heading wins when it names the service as written; else the line's own words, else the heading's help
    const own = bestMatch(text, options.services);
    const underHeading = context.heading ? bestMatch(`${context.heading} ${text}`, options.services) : null;
    const service = (underHeading?.exact ? underHeading : null) ?? own ?? underHeading;
    const place = bestMatch(text, options.places) ?? (context.intro || context.heading ? bestMatch(`${context.intro ?? ""} ${context.heading ?? ""}`, options.places) : null);
    const resource = bestMatch(text, options.resources);
    return {
        text: text.replace(/\s+/g, " ").trim().slice(0, 200),
        service: service?.value ?? null,
        place: place?.value ?? null,
        resource: resource?.value ?? null,
        unit: unitOf(text),
        currency: currencyOf(text),
        amount,
        quantity: null,
        predicted: { service: !!service && !service.exact, place: !!place && !place.exact, resource: !!resource && !resource.exact },
        ...(context.heading || context.intro ? { context: [context.heading, context.intro].filter(Boolean).join(" · ").slice(0, 400) } : {}),
        ...extra,
    };
};

/**
 * Pasted lines that each carry one price ("B6 Land Cruiser, Dubai — 1,250 AED
 * per day"). The amount is the last number in the line that isn't a year or
 * part of a word.
 */
export const linesFromText = (text: string, options: PriceListOptions): ProposedPriceLine[] => {
    // the words around the priced lines: the intro above the first one, and each section's heading
    let intro = "";
    let heading = "";
    let seenPrice = false;
    // a WhatsApp export: each message without its stamp and sender ("[15/10/2026, 09:13] Omar: …" isn't priced 2026.09)
    const plain = chatSenders(text).length ? withoutChatStamps(text) : text;
    return plain.split(/\r?\n/).flatMap((raw) => {
        const trimmed = raw.trim();
        if (!trimmed) return [];
        const found = pricedLine(trimmed);
        if (found === null) {
            if (!seenPrice) intro = `${intro} ${trimmed}`.trim().slice(0, 400);
            // a short title line ("Secure Transport"), not a greeting or a sentence
            if (trimmed.split(/\s+/).length <= 6 && !/[.:]$/.test(trimmed) && !/^(hi|hello|dear|hey|regards|thanks)\b/i.test(trimmed)) heading = trimmed;
            return [];
        }
        seenPrice = true;
        return [line(trimmed, found, options, {}, { heading, intro })];
    });
};

/** The price on a pasted line (the last number that isn't a year, date, time, phone or count), or null. */
const pricedLine = (trimmed: string): number | null => {
    const numbers = Array.from(trimmed.matchAll(/(?<![\p{L}\d])\d[\d.,\s]*\d(?![\p{L}\d])|(?<![\p{L}\d])\d(?![\p{L}\d])/gu))
        .map((match) => ({ raw: match[0].trim(), at: match.index ?? 0, amount: parseAmount(match[0]) }))
        .filter((hit) => {
            if (hit.amount === null) return false;
            const before = trimmed.slice(Math.max(0, hit.at - 12), hit.at);
            const after = trimmed.slice(hit.at + hit.raw.length, hit.at + hit.raw.length + 6);
            const isYear = Number.isInteger(hit.amount) && hit.amount >= 1990 && hit.amount <= 2100 && !/[.,]/.test(hit.raw);
            // phone numbers (+971…, 00962…) and long reference / ID numbers
            const isPhoneOrId = /(\+|\b00)\s*$/.test(before) || hit.raw.replace(/\D/g, "").length >= 9;
            // "31 Dec", "Dec 31"
            const isDate = new RegExp(`^\\s*(${MONTHS})`, "i").test(after) || new RegExp(`(${MONTHS})\\.?\\s*$`, "i").test(before);
            // "07:00", "09:14" — part of a time (written as one: no spaces, two-digit minutes); "T3: 450" stays a price
            const isTime = (/\d{1,2}:$/.test(before) && /^\d{2}$/.test(hit.raw)) || (/^:\d{2}(?!\d)/.test(after) && /^\d{1,2}$/.test(hit.raw));
            // "25%", "10 hours", "3 travellers" — a share, a duration or a head count
            const isCount = /^\s*(%|hours?\b|hrs?\b|minutes?\b|mins?\b|people\b|persons?\b|pax\b|travell?ers?\b|guests?\b)/i.test(trimmed.slice(hit.at + hit.raw.length, hit.at + hit.raw.length + 14));
            return !isYear && !isPhoneOrId && !isDate && !isTime && !isCount;
        });
    const last = numbers[numbers.length - 1];
    return last ? last.amount : null;
};

const HEADERS: Record<"text" | "place" | "unit" | "currency" | "amount" | "quantity", RegExp> = {
    text: /^(item|description|service|services|vehicle|resource|name|details?|product|type|category)$/i,
    place: /^(place|location|city|area|region|country|zone|route)$/i,
    unit: /^(unit|units|per|basis|rate type|uom)$/i,
    currency: /^(currency|ccy|cur|curr)$/i,
    amount: /^(price|rate|cost|amount|tariff|fee|charge|unit price|unit cost|day rate|daily rate)(\s*\(.*\))?$/i,
    quantity: /^(qty|quantity|count|no\.?)$/i,
};

/**
 * Rows from a spreadsheet: finds the header row (in the first 10 rows) that
 * names a price column and at least one describing column, then reads every
 * row under it. Returns null when there's no such header — then the rows go
 * to "Read with AI" as text.
 * @param rows - The first sheet as rows of cell text.
 */
export const linesFromRows = (rows: string[][], options: PriceListOptions): ProposedPriceLine[] | null => {
    for (let headerAt = 0; headerAt < Math.min(rows.length, 10); headerAt += 1) {
        const headers = rows[headerAt].map((cell) => String(cell ?? "").trim());
        const column = (key: keyof typeof HEADERS) => headers.findIndex((header) => HEADERS[key].test(header));
        const amountAt = column("amount");
        const textAt = column("text");
        if (amountAt < 0 || textAt < 0) continue;
        const placeAt = column("place");
        const unitAt = column("unit");
        const currencyAt = column("currency");
        const quantityAt = column("quantity");
        const currencyInHeader = currencyOf(headers[amountAt]);
        return rows.slice(headerAt + 1).flatMap((row) => {
            const cell = (at: number) => (at >= 0 ? String(row[at] ?? "").trim() : "");
            const amountText = cell(amountAt);
            const amount = parseAmount(amountText);
            // every describing column (Service, Resource, Vehicle…), then the place
            const textCells = headers.map((header, at) => (HEADERS.text.test(header) && at !== amountAt ? cell(at) : "")).filter(Boolean);
            const description = [...textCells, cell(placeAt)].filter(Boolean).join(", ");
            if (!description && amount === null) return [];
            const unitText = cell(unitAt);
            const quantity = parseAmount(cell(quantityAt));
            const placeHit = bestMatch(cell(placeAt) || description, options.places);
            const read = line(description || row.filter(Boolean).join(" "), amount, options);
            return [line(description || row.filter(Boolean).join(" "), amount, options, {
                place: placeHit?.value ?? null,
                predicted: { ...read.predicted, place: !!placeHit && !placeHit.exact },
                unit: unitOf(unitText) ?? unitOf(description),
                currency: currencyOf(cell(currencyAt)) ?? currencyOf(amountText) ?? currencyInHeader ?? currencyOf(description),
                quantity: quantity !== null && quantity > 0 ? quantity : null,
            })];
        });
    }
    return null;
};

/** Spreadsheet rows as tab-separated text, for "Read with AI" (capped). */
export const rowsAsText = (rows: string[][], max = 20000): string =>
    rows.map((row) => row.map((cell) => String(cell ?? "").replace(/\s+/g, " ").trim()).join("\t")).join("\n").slice(0, max);

/** A CSV file's rows (simple quoting). */
export const csvRows = (text: string): string[][] =>
    text.split(/\r?\n/).filter((row) => row.trim()).map((row) => {
        const cells: string[] = [];
        let current = "";
        let quoted = false;
        for (let i = 0; i < row.length; i += 1) {
            const ch = row[i];
            if (ch === "\"" && row[i + 1] === "\"" && quoted) { current += "\""; i += 1; } else if (ch === "\"") quoted = !quoted;
            else if ((ch === "," || ch === ";" || ch === "\t") && !quoted) { cells.push(current); current = ""; } else current += ch;
        }
        cells.push(current);
        return cells;
    });

/**
 * The first worksheet of an .xlsx file as rows of cell text. Parsed with
 * exceljs, never xlsx (published parse-path CVEs — see importExcelHelper).
 */
export const xlsxRows = async (buffer: ArrayBuffer, maxRows = 500): Promise<string[][]> => {
    const ExcelJS = await import("exceljs");
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const sheet = workbook.worksheets[0];
    if (!sheet) return [];
    const plain = (value: any): string => {
        if (value === null || value === undefined) return "";
        if (value instanceof Date) return value.toISOString().slice(0, 10);
        if (typeof value === "object") {
            if ("result" in value) return plain(value.result);
            if ("richText" in value) return value.richText.map((part: any) => part.text).join("");
            if ("text" in value) return String(value.text);
            return "";
        }
        return String(value);
    };
    const rows: string[][] = [];
    sheet.eachRow({ includeEmpty: false }, (row) => {
        if (rows.length >= maxRows) return;
        const cells: string[] = [];
        row.eachCell({ includeEmpty: true }, (cell, col) => { cells[col - 1] = plain(cell.value); });
        rows.push(Array.from(cells, (cell) => cell ?? ""));
    });
    return rows;
};

/** What kind of file it is, for the reader. */
export const fileKind = (file: File): "sheet" | "csv" | "image" | "pdf" | "old-excel" | "other" => {
    const name = file.name.toLowerCase();
    if (name.endsWith(".xlsx")) return "sheet";
    if (name.endsWith(".xls")) return "old-excel";
    if (name.endsWith(".csv") || file.type === "text/csv") return "csv";
    if (file.type.startsWith("image/")) return "image";
    if (file.type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
    return "other";
};
